/**
 * ============================================================================
 * PrimaryLayoutChrome (MVC View — split for streaming)
 * ============================================================================
 *
 * Purpose: Header / sticky top chrome and footer / widget bottom chrome.
 * Split so (primary)/layout can Suspense around data without blocking {children}.
 * ------------------------------------------------------------
 * Developer Name : Praveen Tripathi
 * Purpose        : Primary layout top/bottom chrome with safe inline data script
 * ------------------------------------------------------------
 */

"use client";

import { memo, useEffect, useState } from "react";
import { ModalTypes } from "@/types/modal";
import type { AssociatedProgramInterface } from "@/components/organisms/HeaderNew";
import type { PrimaryLayoutViewModel } from "../model/primary-layout.types";
import { deferThirdPartyScript } from "@/helpers/deferThirdPartyScript";
import { injectInlineScript } from "@/helpers/injectInlineScript";
import {
  EnrollNowSticky,
  FooterNew,
  HeaderNew,
  SideButtons,
  StickyWidget,
} from "./layout-dynamic";

/** Escape `<` so JSON cannot close a surrounding <script> tag. */
function toInlineJson(value: unknown): string {
  const raw =
    typeof value === "string" ? value : JSON.stringify(value ?? null);
  return raw.replace(/</g, "\\u003c");
}

function useDeferredLayoutWidgets() {
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    return deferThirdPartyScript(() => {
      setShouldMount(true);
    });
  }, []);

  return shouldMount;
}

type ChromeProps = {
  viewModel: PrimaryLayoutViewModel;
};

function PrimaryLayoutTopBase({ viewModel }: ChromeProps) {
  const shouldMountWidgets = useDeferredLayoutWidgets();
  const {
    device,
    isMobile,
    headerMenu,
    headerMenuList,
    pageType,
  } = viewModel;

  const associatedPrograms =
    headerMenu.associatedPrograms as AssociatedProgramInterface[];

  return (
    <>
      <HeaderNew
        associatedPrograms={associatedPrograms}
        tickyBarPageType="home"
        isMobile={device !== "desktop"}
        headerMenuList={headerMenuList}
        popularCourses={headerMenu.PopularCourses}
        newLaunches={headerMenu.NewLaunches}
        isHomePage={true}
      />
      {!isMobile && shouldMountWidgets && (
        <SideButtons
          associatedPrograms={associatedPrograms}
          modalType={ModalTypes.ConnectModal}
          pageType={pageType}
        />
      )}
      {isMobile && (
        <EnrollNowSticky
          associatedPrograms={associatedPrograms}
          isHomePage={true}
          forceHomeVariant
        />
      )}
    </>
  );
}

function PrimaryLayoutBottomBase({ viewModel }: ChromeProps) {
  const shouldMountWidgets = useDeferredLayoutWidgets();
  const {
    device,
    headerMenu,
    footerData,
    headerFooterData,
    cityListingPages
  } = viewModel;

  const associatedPrograms =
    headerMenu.associatedPrograms as AssociatedProgramInterface[];

  const headerFooterJson = toInlineJson(headerFooterData);

  useEffect(() => {
    injectInlineScript(
      "header-footer-data",
      `window.headerFooterData = ${headerFooterJson};`,
    );
  }, [headerFooterJson]);

  return (
    <>
      {shouldMountWidgets && (
        <>
          <StickyWidget
            associatedPrograms={associatedPrograms}
          />
        </>
      )}
      <FooterNew
        footerData={footerData}
        cityListingPages={cityListingPages}
        device={device}
      />
    </>
  );
}

export const PrimaryLayoutTop = memo(PrimaryLayoutTopBase);
PrimaryLayoutTop.displayName = "PrimaryLayoutTop";

export const PrimaryLayoutBottom = memo(PrimaryLayoutBottomBase);
PrimaryLayoutBottom.displayName = "PrimaryLayoutBottom";
